import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { loadSettings } from '@/config/settings';
import { drawArcadeBackground } from '@/utils/arcadeBackground';
import { VFXManager } from '@/objects/VFXManager';
import { AudioManager } from '@/systems/AudioManager';
import { DIFFICULTIES, getInputThresholdMs, type DifficultyConfig } from '@/utils/timing';
import type { MorseSymbol } from '@/utils/morse';
import { calculateHorizontalLayouts, type LaneLayout } from '@/game/LaneLayoutManager';
import { PlayerLane } from '@/game/PlayerLane';
import { InputManager } from '@/systems/InputManager';
import type { SinglePlayerGameData } from '@/types/multiplayer';
import { mountAccountBadge } from '@/ui/accountBadge';
import { setDiscordSoloPresence } from '@/discord';
import { getLocalSeatColor } from '@/utils/seatColors';
import { trackGameStart } from '@/services/analytics';

export class SingleGameScene extends Phaser.Scene {
  private levelIndex!: number;
  private message!: string;
  private difficultyConfig!: DifficultyConfig;
  private difficultyKey: string = 'medium';

  private lane!: PlayerLane;
  private layout!: LaneLayout;
  private extraInputManagers: InputManager[] = [];

  private vfxManager!: VFXManager;
  private audioManager!: AudioManager;

  private isPlaying: boolean = false;
  private gameStartTime: number = 0;
  private notesFinished: boolean = false;
  private notesFinishedTime: number = 0;
  private inputDisabled: boolean = false;
  private inputDisabledTime: number = 0;
  private readonly INPUT_GRACE_PERIOD = 2000;
  private activeToneHolders: number = 0;

  // These labels should read as global “playfield headers”, not lane-local annotations.
  private readonly LABEL_Y = 160;

  constructor() {
    super({ key: 'SingleGameScene' });
  }

  init(data: SinglePlayerGameData): void {
    if (data.levelIndex === undefined || data.levelIndex === null) {
      throw new Error('SingleGameScene requires levelIndex');
    }
    if (!data.message) {
      throw new Error('SingleGameScene requires message');
    }
    if (!data.difficulty) {
      throw new Error('SingleGameScene requires difficulty');
    }
    if (!DIFFICULTIES[data.difficulty]) {
      throw new Error(`Invalid difficulty: "${data.difficulty}"`);
    }

    this.levelIndex = data.levelIndex;
    this.message = data.message;
    this.difficultyKey = data.difficulty;
    this.difficultyConfig = DIFFICULTIES[this.difficultyKey];

    this.isPlaying = false;
    this.gameStartTime = 0;
    this.notesFinished = false;
    this.notesFinishedTime = 0;
    this.inputDisabled = false;
    this.inputDisabledTime = 0;
    this.activeToneHolders = 0;
    this.extraInputManagers = [];
  }

  create(): void {
    // Track game start
    trackGameStart({ mode: 'solo', levelIndex: this.levelIndex, difficulty: this.difficultyKey });

    // Discord Activity presence (solo play)
    setDiscordSoloPresence({ difficulty: this.difficultyKey, messageNumber: this.levelIndex + 1 });

    this.cameras.main.fadeIn(300, 5, 7, 22);
    this.createBackground();
    mountAccountBadge(this);

    this.layout = calculateHorizontalLayouts(1)[0];

    this.vfxManager = new VFXManager(this);
    this.audioManager = AudioManager.get(this);
    this.audioManager.playSfx('start_game');

    this.createLane();
    this.lane.showCountdown().then(() => this.startGame());

    this.input.keyboard?.on('keydown-ESC', () => {
      this.endGame();
    });
  }

  update(time: number, delta: number): void {
    if (!this.isPlaying) return;

    this.lane.update(time, delta);
    this.checkPassedNotes();

    if (this.lane.noteTrack.isComplete() && !this.notesFinished) {
      this.notesFinished = true;
      this.notesFinishedTime = time;
    }

    if (this.notesFinished && !this.inputDisabled) {
      const timeSinceNotesFinished = time - this.notesFinishedTime;
      if (timeSinceNotesFinished > this.INPUT_GRACE_PERIOD) {
        this.inputDisabled = true;
        this.inputDisabledTime = time;
        this.lane.stopInput();
        for (const manager of this.extraInputManagers) {
          manager.disable();
        }
        this.activeToneHolders = 0;
        this.audioManager.stopTone();
      }
    }

    if (this.inputDisabled) {
      const timeSinceDisabled = time - this.inputDisabledTime;
      if (timeSinceDisabled > 250 && this.lane.sentTrack.isEmpty()) {
        this.endGame();
      }
    }
  }

  shutdown(): void {
    this.lane?.noteTrack.destroy();
    this.lane?.destroy();
    this.vfxManager?.destroy();
    this.audioManager?.destroy();
    for (const manager of this.extraInputManagers) {
      manager.destroy();
    }
    this.extraInputManagers = [];
  }

  private createBackground(): void {
    drawArcadeBackground(this);

    const hudBar = this.add.rectangle(GAME_WIDTH / 2, 60, GAME_WIDTH, 120, THEME.colors.backgroundLightHex, 0.68);
    hudBar.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    hudBar.setDepth(-1);

    const footer = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT - 40, GAME_WIDTH, 80, THEME.colors.backgroundLightHex, 0.68);
    footer.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    footer.setDepth(-1);

    this.add.text(GAME_WIDTH / 2, 22, `SINGLE — ${this.difficultyConfig.name} Mode`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5, 0);

    this.add.text(40, this.LABEL_Y, 'TRANSMITTED:', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    });

    this.add.text(GAME_WIDTH - 40, this.LABEL_Y, 'TARGET:', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(1, 0);

    const settings = loadSettings();
    const keyLabel = settings.inputBindings[0]?.type === 'keyboard'
      ? (settings.inputBindings[0].key === 'SPACE' ? 'SPACE' : settings.inputBindings[0].key)
      : settings.inputKey;

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 52,
      `${keyLabel} or LMB — TAP for dot, HOLD for dash`, {
        fontFamily: THEME.fonts.body,
        fontSize: THEME.sizes.bodyLarge,
        color: THEME.colors.text,
      }).setOrigin(0.5);

    this.add.text(GAME_WIDTH - 20, GAME_HEIGHT - 20, 'ESC = quit', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    }).setOrigin(1, 0.5);
  }

  private createLane(): void {
    const settings = loadSettings();
    const binding = settings.inputBindings[0]?.type === 'keyboard'
      ? settings.inputBindings[0]
      : { playerId: 0, type: 'keyboard' as const, key: settings.inputKey };

    this.lane = new PlayerLane(this, {
      layout: this.layout,
      playerName: 'Player',
      isSinglePlayer: true,
      inputBinding: binding,
      difficulty: this.difficultyConfig,
      noteMessage: this.message,
      targetMessage: this.message,
      borderColor: getLocalSeatColor(0, 1, [0]) ?? THEME.colors.borderHex,
      vfxManager: this.vfxManager,
      audioManager: this.audioManager,
    });

    this.lane.inputManager.onInputDown((time) => this.handleInputDown(time));
    this.lane.inputManager.onInputUp((time, duration) => this.handleInputUp(time, duration));

    const mouseInput = new InputManager(this, { playerId: 0, type: 'mouse', mouseButton: 0 });
    mouseInput.onInputDown((time) => this.handleInputDown(time));
    mouseInput.onInputUp((time, duration) => this.handleInputUp(time, duration));
    this.extraInputManagers.push(mouseInput);
  }

  private startGame(): void {
    this.isPlaying = true;
    this.gameStartTime = this.time.now;
    this.lane.noteTrack.start(this.gameStartTime);
    this.lane.sentTrack.start();
    this.lane.inputManager.enable();
    for (const manager of this.extraInputManagers) {
      manager.enable();
    }
  }

  private handleInputDown(time: number): void {
    if (!this.isPlaying) return;

    this.lane.setHitZonePressed(true);

    this.activeToneHolders++;
    if (this.activeToneHolders === 1) {
      this.audioManager.startTone();
    }

    const downResult = this.lane.handleKeyDown(time);
    if (!downResult.note) {
      return;
    }
    if (downResult.timingFeedback === 'EARLY') {
      this.lane.showFeedbackText('EARLY', THEME.colors.yellow);
    } else if (downResult.timingFeedback === 'LATE') {
      this.lane.showFeedbackText('LATE', THEME.colors.hit.ok);
    }
  }

  private handleInputUp(time: number, duration: number): void {
    if (!this.isPlaying) return;

    this.lane.setHitZonePressed(false);

    this.activeToneHolders = Math.max(0, this.activeToneHolders - 1);
    if (this.activeToneHolders === 0) {
      this.audioManager.stopTone();
    }

    const threshold = getInputThresholdMs(this.difficultyConfig);
    const playerSymbol: MorseSymbol = duration < threshold ? '.' : '-';

    const upResult = this.lane.handleKeyUp(time, duration, playerSymbol);

    if (!upResult.note) {
      return;
    }
  }

  private checkPassedNotes(): void {
    this.lane.checkPassedNotes();
  }

  private endGame(): void {
    this.isPlaying = false;
    this.audioManager.stopTone();
    this.activeToneHolders = 0;

    this.lane.stopInput();
    for (const manager of this.extraInputManagers) {
      manager.disable();
    }

    const results = this.lane.getResults();
    const streak = this.lane.getMaxStreak();
    const judge = this.lane.getJudgeResults();

    this.cameras.main.fadeOut(300, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ResultScene', {
        score: results.score,
        streak,
        decodedMessage: this.lane.sentTrack.getFormattedDecodedMessage(),
        intendedMessage: this.message,
        accuracy: results.accuracy,
        grade: results.grade,
        levelIndex: this.levelIndex,
        difficulty: this.difficultyKey,
        judge,
      });
    });
  }
}
